<script setup lang="ts">
  import type { TenantUser } from '@gat-ship/types'
  import type {
    ComboboxFieldItem,
    ComboboxFieldProps
  } from '@gat-ship/vue-components'
  import { Avatar, AvatarFallback, AvatarImage } from '@gat-ship/vue-components'
  import { computed, onMounted, ref } from 'vue'
  import { useTenantUserUtils } from '../../composables/useTenantUserUtils'
  import { useTenantUserSelectors } from '../../composables/useTenantUserSelectors'
  import {
    useTenantUsersCollection,
    useTenantUsersCollectionContext
  } from '../../composables/useTenantUsersCollection'
  import { ComboboxField, useAppLoading } from '@gat-ship/vue-components'
  import { tenantUserLoadingIds } from '../../composables/useTenantUserDetail'
  import { IconLoading } from '@gat-ship/icons'

  export interface SelectAgencyProps extends Omit<
    ComboboxFieldProps,
    'items' | 'modelValue'
  > {
    items?: TenantUser[]
    showAvatar?: boolean
    dataSource?: 'internal' | 'provider'
    preSelectedUser?: TenantUser
    showInactive?: boolean
  }

  const props = withDefaults(defineProps<SelectAgencyProps>(), {
    showAvatar: true,
    showInactive: false
  })

  const { TenantUserUtils } = useTenantUserUtils()
  const { selectTenantUserOptions, tenantUserDisplayTitle } =
    useTenantUserSelectors()

  const localDataSource = ref<
    ReturnType<typeof useTenantUsersCollection> | undefined
  >(undefined)

  function setDatasource() {
    try {
      if (props.dataSource === 'internal') {
        localDataSource.value = useTenantUsersCollection()
      } else if (props.dataSource === 'provider') {
        const provided = useTenantUsersCollectionContext()
        if (!provided) {
          throw new Error('No tenant users provider found')
        }
        localDataSource.value = provided
      } else {
        throw new Error(`Invalid data source: ${props.dataSource}`)
      }
    } catch (error) {
      if (props.dataSource === 'provider') {
        console.warn(
          'Error getting tenant users provider. Using internal data source.',
          error
        )
      }
      localDataSource.value = useTenantUsersCollection()
    }
  }

  onMounted(() => {
    setDatasource()
  })

  const selectedUserId = defineModel<TenantUser['userId'] | undefined>({
    required: true
  })

  const selectedUser = computed(() => {
    return (localDataSource.value?.tenantUsers.find(
      (i) => i.userId === selectedUserId.value
    ) ?? props.preSelectedUser?.userId === selectedUserId.value)
      ? props.preSelectedUser
      : undefined
  })

  const isOpen = defineModel<boolean>('open', { default: false })

  function getTitle(item: TenantUser) {
    return tenantUserDisplayTitle(
      item,
      localDataSource.value?.tenantUsers ?? []
    )
  }

  const _items = computed<ComboboxFieldItem<TenantUser>[]>(() =>
    selectTenantUserOptions(localDataSource.value?.tenantUsers ?? [], {
      showInactive: props.showInactive,
      preSelectedUser: props.preSelectedUser
    }).map((user) => ({
      title: user.fullName,
      value: user.userId,
      searchValue: `${user.fullName} ${user.initials}`,
      metaData: user
    }))
  )

  const { isLoading } = useAppLoading()
</script>
<template>
  <ComboboxField
    v-bind="props"
    v-model="selectedUserId"
    v-model:open="isOpen"
    :data-testid="props.dataTestid ?? 'select-agency-group-input'"
    :items="_items"
    @update:open.once="
      () => {
        localDataSource?.fetchTenantUsers?.()
      }
    "
  >
    <div v-if="selectedUser">
      <div class="flex grow items-center gap-2">
        <Avatar
          v-if="showAvatar"
          class="relative size-6"
          :class="{
            'opacity-60': !TenantUserUtils.isActiveTenantUser(selectedUser)
          }"
        >
          <IconLoading
            v-if="
              isLoading(tenantUserLoadingIds.tenantUser(selectedUser.userId))
            "
            class="absolute left-1/2 top-1/2 size-4 -translate-x-1/2 -translate-y-1/2 text-white"
          />
          <AvatarImage
            :src="
              selectedUser.imageUrl ??
              _items.find((i) => i.value === selectedUser?.userId)?.metaData
                .imageUrl ??
              ''
            "
          />
          <AvatarFallback>{{ selectedUser.initials }}</AvatarFallback>
        </Avatar>
        <div>{{ getTitle(selectedUser).name }}</div>
      </div>
    </div>
    <template #item="{ item }">
      <div
        class="flex min-w-[200px] grow items-center gap-2"
        :class="{
          'opacity-60': !TenantUserUtils.isActiveTenantUser(item.metaData)
        }"
      >
        <Avatar class="size-8">
          <AvatarImage
            :src="
              item.metaData.imageUrl ??
              _items.find((i) => i.value === selectedUser?.userId)?.metaData
                .imageUrl ??
              ''
            "
          />
          <AvatarFallback>{{ item.metaData.initials }}</AvatarFallback>
        </Avatar>
        <div>{{ getTitle(item.metaData).name }}</div>
      </div>
    </template>
  </ComboboxField>
</template>
